import {IAddressResponseData, CountriesFactory} from "../../../../typescript-library";
import * as React from "react";
import {PropsWithChildren, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {getShopId} from "../../../lib/getShopId";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {isSameAddress} from "../AddressStep/AddressStep";
import summaryStyles from "./Summary.module.scss";

interface ISummaryAddressProps {
  goToAddressView?: () => void;
}

interface IAddressInformation {
  deliveryCountryReadable?: string;
  invoiceCountryReadable?: string;
}

const SummaryAddress: React.FC<ISummaryAddressProps> = (props) => {
  const {checkout} = useCheckoutContext();
  const {t} = useTranslation(["errorcodes", "translations"]);
  const [addressInfo, setAddressInfo] = useState<IAddressInformation>({});
  const {deliveryAddress, invoiceAddress} = checkout;
  const {goToAddressView} = props;
  const areAddressesSame = isSameAddress(checkout.invoiceAddress, checkout.deliveryAddress);

  /* retrieve readable country names from codes */
  useEffect(() => {
    if (!invoiceAddress || !deliveryAddress) {
      return;
    }
    void CountriesFactory.getAllCountries(getShopId()).then(() => {
      const invoiceCountryLookup = CountriesFactory.getCountryByCode(invoiceAddress.countryCode);
      const deliveryCountryLookup = CountriesFactory.getCountryByCode(deliveryAddress.countryCode);

      setAddressInfo({
        deliveryCountryReadable: deliveryCountryLookup?.name,
        invoiceCountryReadable: invoiceCountryLookup?.name,
      });
    });
  }, []);

  /*
   * delivery address paragraph preparation
   */
  const DeliveryParagraph = () => {
    const title = areAddressesSame
      ? `${t("general.invoiceAddress")}/${t("general.deliveryAddress")}`
      : t("general.deliveryAddress");

    return (
      <section>
        <AddressWrapper showArrowBorder={true} goToAddressView={goToAddressView} title={title}>
          {!deliveryAddress ? (
            <p>{t("checkout.emptyDeliveryAddress")}</p>
          ) : (
            <Paragraph
              address={deliveryAddress}
              areSameAddresses={areAddressesSame}
              humanReadableCountry={addressInfo.deliveryCountryReadable}
              isInvoice={false}
            />
          )}
        </AddressWrapper>
      </section>
    );
  };

  /*
   * if addresses are the same, just show one
   * no need to prepare invoice address
   */
  if (areAddressesSame) {
    return <DeliveryParagraph />;
  }

  // invoice address paragraph preparation
  const InvoiceParagraph: React.FC = () => {
    return (
      <AddressWrapper goToAddressView={goToAddressView} title={t("general.invoiceAddress")}>
        {!invoiceAddress ? (
          <p>{t("checkout.emptyInvoiceAddress")}</p>
        ) : (
          <Paragraph
            address={invoiceAddress}
            humanReadableCountry={addressInfo.invoiceCountryReadable}
            isInvoice={true}
          />
        )}
      </AddressWrapper>
    );
  };

  return (
    <React.Fragment>
      <InvoiceParagraph />
      <DeliveryParagraph />
    </React.Fragment>
  );
};

export default SummaryAddress;

/**
 * React address container
 * @param props
 * @constructor
 */
const AddressWrapper: React.FC<
  PropsWithChildren<{
    goToAddressView?: () => void;
    showArrowBorder?: boolean;
    title: string;
  }>
> = ({children, goToAddressView, showArrowBorder = true, title}) => {
  const {t} = useTranslation(["errorcodes", "translations"]);

  return (
    <section>
      <h2>{title}</h2>
      {goToAddressView && (
        <button type="button" className={summaryStyles.pen} onClick={goToAddressView}>
          <span className="icon icon-pencil--blue icon-size--20">&#160;</span>
          <span className="visually-hidden">{t("checkout.editAddress")}</span>
        </button>
      )}
      <div className={showArrowBorder ? "ArrowBorder" : undefined}>{children}</div>
    </section>
  );
};

/**
 * React address paragraph
 * @param props
 * @constructor
 */
const Paragraph: React.FC<{
  address: IAddressResponseData;
  areSameAddresses?: boolean;
  humanReadableCountry?: string;
  isInvoice: boolean;
}> = (props) => {
  const {address, areSameAddresses, humanReadableCountry, isInvoice} = props;
  const {t} = useTranslation(["errorcodes", "translations"]);
  const {city, company, department, email, firstName, lastName, phone, state, street, zip, addressSupplement} = address;
  const streetNo = props.address.number;

  const ConditionalLine = ({text, condition = true}: {text: string | null; condition?: boolean}) => {
    if (!text || !condition) return;
    return (
      <>
        {text} <br />
      </>
    );
  };

  return (
    <div>
      <p>
        <ConditionalLine text={company} />
        <ConditionalLine text={department} />
        <ConditionalLine text={`${firstName} ${lastName}`} />
        <ConditionalLine
          condition={!!addressSupplement && (street === "Postfiliale" || street === "Packstation")}
          text={`Postnummer: ${addressSupplement}`}
        />
        {["GB", "FR", "US"].includes(address.countryCode) ? (
          <ConditionalLine text={`${streetNo} ${street}`} />
        ) : (
          <ConditionalLine text={`${street} ${streetNo}`} />
        )}
        {`${zip} ${city}`} <br />
        {!!state ? `${state}, ${humanReadableCountry || ""}` : humanReadableCountry}
      </p>
      <p>
        <ConditionalLine condition={isInvoice || areSameAddresses} text={t("form.email--email", {email})} />
        <ConditionalLine condition={!!phone} text={t("form.phone--phone", {phone})} />
      </p>
    </div>
  );
};
